/* =========================================================================
   Phrase Studios — Polish & motion layer
   Adds the Flaneur-level micro-details: parallax, number tickers,
   scroll-linked reveals, custom cursor. (Scroll-progress rail and magnetic
   cursor-follow CTAs removed; CTA hover is now a subtle CSS lift.)
   Everything is feature-detected and gated by prefers-reduced-motion.
   ========================================================================= */
(function () {
  'use strict';

  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const isFinePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const prefersReduced = motionQuery.matches;

  if (prefersReduced) return;

  // -------------------------------------------------------------------------
  // Lenis — inertia scroll. Desktop fine-pointer only; mobile/touch keep
  // native momentum. data-lenis-prevent opts an element out (cart drawer,
  // filter sheet, modal scroll containers).
  // -------------------------------------------------------------------------
  function initLenis() {
    if (!isFinePointer) return;
    if (typeof window.Lenis !== 'function') return;
    if (window.__phraseLenis) return;

    const lenis = new window.Lenis({
      duration: 1.05,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      smoothTouch: false,
      wheelMultiplier: 1,
      touchMultiplier: 1.4,
      autoResize: true,
      prevent: (node) => {
        if (!node || node.nodeType !== 1) return false;
        return Boolean(node.closest && node.closest('[data-lenis-prevent]'));
      }
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    window.__phraseLenis = lenis;
    document.documentElement.classList.add('phrase-lenis-on');
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initLenis, { once: true });
  } else {
    initLenis();
  }

  // -------------------------------------------------------------------------
  // (Vertical scroll-progress rail removed — was the thin side line + green pill.)
  // -------------------------------------------------------------------------

  // -------------------------------------------------------------------------
  // Scroll-linked reveals — auto-tag obvious candidates
  // -------------------------------------------------------------------------
  function tagReveals() {
    // Auto-tag: any phrase-* section that hasn't been tagged + has a heading or eyebrow
    const candidates = document.querySelectorAll([
      '.phrase-pdp__section-head',
      '.phrase-pdp__set',
      '.phrase-pdp__community',
      '.phrase-pdp__recs',
      '.phrase-pdp__closer',
      '.phrase-hero__copy',
      '.phrase-about__movement',
      '.phrase-journal__card',
      '.phrase-the-circle__tier',
      '.phrase-care__item',
      '.phrase-vault__card'
    ].join(','));
    candidates.forEach((el) => {
      if (!el.classList.contains('phrase-reveal')) {
        el.classList.add('phrase-reveal');
      }
    });
  }
  tagReveals();

  function initRevealObserver() {
    if (!('IntersectionObserver' in window)) {
      // No-op: show everything immediately
      document.querySelectorAll('.phrase-reveal, .phrase-eyebrow-reveal').forEach((el) => {
        el.classList.add('is-revealed');
      });
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -60px 0px' }
    );
    document.querySelectorAll('.phrase-reveal, .phrase-eyebrow-reveal').forEach((el) => {
      io.observe(el);
    });
  }
  initRevealObserver();

  // -------------------------------------------------------------------------
  // Magnetic CTAs — auto-tag primary buttons + handle motion
  // -------------------------------------------------------------------------
  function tagMagnetic() {
    const targets = document.querySelectorAll([
      '.phrase-pdp__atc',
      '.phrase-hero__cta',
      'a.phrase-button--primary',
      'button.phrase-button--primary',
      'a.phrase-btn-primary',
      'button.phrase-btn-primary'
    ].join(','));
    targets.forEach((el) => {
      if (!el.hasAttribute('data-phrase-magnetic')) {
        el.setAttribute('data-phrase-magnetic', '');
      }
    });
  }
  tagMagnetic();
  // Cursor-follow ("magnetic") motion removed per brand direction — buttons no
  // longer chase the pointer. The [data-phrase-magnetic] attribute is kept as a
  // styling hook; the new behaviour is a subtle hover lift handled in CSS
  // (see phrase-polish.css → "CTA hover lift").

  // -------------------------------------------------------------------------
  // Hero parallax
  // -------------------------------------------------------------------------
  function initParallax() {
    const targets = document.querySelectorAll('[data-phrase-parallax]');
    if (targets.length === 0) return;
    let raf = null;
    function update() {
      raf = null;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      targets.forEach((el) => {
        const intensity = parseFloat(el.dataset.phraseParallax || '0.08');
        const rect = el.getBoundingClientRect();
        const distanceFromCenter = (rect.top + rect.height / 2) - window.innerHeight / 2;
        const offset = -distanceFromCenter * intensity;
        el.style.transform = `translate3d(0, ${offset.toFixed(2)}px, 0)`;
      });
    }
    function onScroll() {
      if (raf === null) raf = requestAnimationFrame(update);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }
  initParallax();

  // -------------------------------------------------------------------------
  // Number tickers
  // -------------------------------------------------------------------------
  function initTickers() {
    if (!('IntersectionObserver' in window)) return;
    const tickers = document.querySelectorAll('[data-phrase-ticker]');
    if (tickers.length === 0) return;

    function animate(el) {
      const target = parseFloat(el.dataset.phraseTickerTo || el.textContent || '0');
      if (isNaN(target)) return;
      const duration = parseInt(el.dataset.phraseTickerDuration || '900', 10);
      const decimals = parseInt(el.dataset.phraseTickerDecimals || '0', 10);
      const prefix = el.dataset.phraseTickerPrefix || '';
      const suffix = el.dataset.phraseTickerSuffix || '';
      const start = performance.now();
      function frame(now) {
        const t = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
        const value = (target * eased).toFixed(decimals);
        el.textContent = prefix + value + suffix;
        if (t < 1) requestAnimationFrame(frame);
        else el.textContent = prefix + target.toFixed(decimals) + suffix;
      }
      requestAnimationFrame(frame);
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            animate(entry.target);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.5 }
    );
    tickers.forEach((el) => io.observe(el));
  }
  initTickers();

  // -------------------------------------------------------------------------
  // Card swatch -> primary image swap (collection grid, related products).
  // Hover a colorway swatch and the card's main image previews that variant's
  // featured media. Desktop fine-pointer only — touch users tap through.
  // -------------------------------------------------------------------------
  function initCardSwatchSwap() {
    if (!isFinePointer) return;
    const containers = document.querySelectorAll('[data-phrase-card-swatches]');
    if (!containers.length) return;

    containers.forEach((container) => {
      const card = container.closest('[data-phrase-card]');
      if (!card) return;
      const img = card.querySelector('.phrase-card__img--primary');
      if (!img) return;

      const originalSrc = img.getAttribute('src');
      const originalSrcset = img.getAttribute('srcset') || '';
      let restoreTimer = null;

      container.querySelectorAll('.phrase-card__swatch[data-image]').forEach((swatch) => {
        const nextSrc = swatch.getAttribute('data-image');
        const nextSrcset = swatch.getAttribute('data-srcset') || '';
        if (!nextSrc) return;

        // Preload so the swap is instant on hover
        const pre = new Image();
        pre.src = nextSrc;

        swatch.addEventListener('mouseenter', () => {
          if (restoreTimer) { clearTimeout(restoreTimer); restoreTimer = null; }
          if (img.getAttribute('src') === nextSrc) return;
          img.setAttribute('src', nextSrc);
          if (nextSrcset) img.setAttribute('srcset', nextSrcset);
        });

        swatch.addEventListener('mouseleave', () => {
          // Tiny delay so flicking between adjacent swatches doesn't snap back
          if (restoreTimer) clearTimeout(restoreTimer);
          restoreTimer = setTimeout(() => {
            img.setAttribute('src', originalSrc);
            if (originalSrcset) img.setAttribute('srcset', originalSrcset);
            else img.removeAttribute('srcset');
            restoreTimer = null;
          }, 60);
        });
      });
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCardSwatchSwap, { once: true });
  } else {
    initCardSwatchSwap();
  }

  // -------------------------------------------------------------------------
  // (Custom cursor removed per brand direction — the "+" over images glitched.)
  // -------------------------------------------------------------------------

})();
